import React from 'react';
import { Linkedin } from 'lucide-react';
import { AuthorCard as AuthorCardModel } from '../../types/content';
import ProfileAvatar from '../UI/ProfileAvatar';

export function AuthorStack({
  authors,
  className,
}: {
  authors?: Array<AuthorCardModel | null | undefined> | null;
  className?: string;
}) {
  const list = (authors || []).filter((author): author is AuthorCardModel => !!author?.displayName);
  if (!list.length) return null;
  return (
    <div className={className}>
      {list.length > 1 ? (
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Authors</p>
      ) : null}
      <div className="flex flex-wrap items-start gap-x-10 gap-y-4">
        {list.map((author) => (
          <AuthorCard key={author.authorId || author.displayName} author={author} compact />
        ))}
      </div>
    </div>
  );
}

const AuthorCard: React.FC<{ author: AuthorCardModel; compact?: boolean }> = ({ author, compact }) => {
  return (
    <div className={`flex items-start gap-3 ${compact ? '' : 'p-4 rounded-2xl border border-gray-200 bg-white'}`}>
      <div className={`${compact ? 'h-10 w-10' : 'h-14 w-14'} rounded-full bg-primary-100 text-primary-700 flex items-center justify-center overflow-hidden shrink-0`}>
        {author.profileImageUrl ? (
          <ProfileAvatar src={author.profileImageUrl} alt={author.displayName} className="h-full w-full object-cover" />
        ) : (
          <span className="font-semibold">{author.displayName?.[0]?.toUpperCase()}</span>
        )}
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-gray-900">{author.displayName}</p>
        {author.professionalTitle && <p className="text-sm text-gray-600">{author.professionalTitle}</p>}
        {!compact && author.biography && <p className="mt-2 text-sm text-gray-600 leading-relaxed">{author.biography}</p>}
        {author.linkedInUrl && (
          <a href={author.linkedInUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary-600 mt-2 hover:underline">
            <Linkedin className="h-4 w-4" />
            LinkedIn
          </a>
        )}
      </div>
    </div>
  );
};

export default AuthorCard;
